import { RefObject, useEffect } from "react";

import { getInstanceByDom } from "hd-echarts/echarts";

/** The card's own geometry animation, plus a margin for the reflow after it. */
const SETTLE_MS = 300;

/**
 * Make the card's chart measure itself once everything around it has settled.
 *
 * The chart sizes itself when its data lands and whenever its own container
 * resizes, which is enough on a dashboard where the widget is already at its
 * final size. Inside the card it is not: the card restores at its persisted
 * geometry while the app is still coming up, animates for 220ms whenever it is
 * maximized or brought back from the dock, and gains a scrollbar the moment
 * its body overflows - each of which changes the box under a chart that has
 * already decided how tall its plot is. What the user sees is a plot drawn to
 * the old size with dead space between it and the legend.
 *
 * So the card asks the chart to measure again at each of those moments. It is
 * the chart's own resize, reached through echarts' public
 * `getInstanceByDom` - the same module the chart was created with, so it is
 * the same instance - rather than a faked resize event, and it is idempotent:
 * a resize to the size it already has does nothing, and before the chart
 * exists there is no instance to find.
 *
 * Card-only. Nothing here touches a dashboard chart.
 */
export function useChartSettleResize(hostRef: RefObject<HTMLElement>): void {
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const resize = () => {
      const canvasHost = host.querySelector<HTMLElement>(".echarts-wrapper");
      if (!canvasHost) return;
      // The card keeps the chart mounted on the tabs that do not show it, in a
      // `hidden` wrapper, and a hidden box measures zero. Resizing to that
      // would throw the plot away, so the chart is left at the size it had -
      // and the observer below fires again when the box comes back.
      if (host.closest("[hidden]")) return;
      getInstanceByDom(canvasHost)?.resize();
    };

    // The frame after this one, when the first layout has been through.
    const frame = requestAnimationFrame(resize);
    // And once the card's animation and the reflow behind it are done.
    const settled = window.setTimeout(resize, SETTLE_MS);

    // Web fonts land after the first paint and change every label's metrics,
    // which is exactly the kind of late reflow the plot has to follow.
    document.fonts?.ready.then(resize).catch(() => undefined);

    // Every later change of the card's own box: maximize, restore from the
    // dock, a drag settling, the body gaining or losing its scrollbar.
    const observer = new ResizeObserver(resize);
    observer.observe(host);

    // The geometry animation ends on the card, and transitionend bubbles up
    // rather than down, so it is heard there.
    const panel = host.closest(".well-card-panel");
    panel?.addEventListener("transitionend", resize);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settled);
      observer.disconnect();
      panel?.removeEventListener("transitionend", resize);
    };
  }, [hostRef]);
}
